import { context, ROOT_CONTEXT, SpanStatusCode, trace } from "@opentelemetry/api";

import type { Attributes, Span, SpanContext, SpanOptions, SpanStatus, Tracer } from "@opentelemetry/api";

const TRACER_NAME = "valerie-health";

export type SpanCallback<T> = (span: Span | undefined) => T | Promise<T>;
export type RunInSpan = <T>(name: string, callback: SpanCallback<T>, options?: SpanOptions) => Promise<T>;

function recordApplicationError(span: Span, error: unknown): void {
  try {
    // Trace data is a separate storage surface from the redacted logger. Avoid
    // copying application error messages, which can contain patient data.
    span.recordException({
      name: error instanceof Error ? error.name : "UnknownError",
      message: "Application error",
    });
    span.setStatus({ code: SpanStatusCode.ERROR });
  } catch {
    // Instrumentation must never replace or obscure the application error.
  }
}

function endSpan(span: Span): void {
  try {
    span.end();
  } catch {
    // Losing the span is preferable to changing application behavior.
  }
}

export function setSpanAttributes(span: Span | undefined, attributes: Attributes): void {
  try {
    span?.setAttributes(attributes);
  } catch {
    // Best-effort telemetry only.
  }
}

export function setSpanStatus(span: Span | undefined, status: SpanStatus): void {
  try {
    span?.setStatus(status);
  } catch {
    // Best-effort telemetry only.
  }
}

/**
 * Build a span runner around a tracer factory. Exported to make the fail-open
 * contract directly testable without installing a global trace provider.
 */
export function createSpanRunner(getTracer: () => Tracer): RunInSpan {
  return async function runInSpan<T>(name: string, callback: SpanCallback<T>, options: SpanOptions = {}): Promise<T> {
    let callbackStarted = false;
    let callbackCompleted = false;
    let callbackFailed = false;
    let callbackResult: T;
    let applicationError: unknown;

    try {
      const tracer = getTracer();

      return await tracer.startActiveSpan(name, options, async (span) => {
        callbackStarted = true;

        try {
          callbackResult = await callback(span);
          callbackCompleted = true;

          return callbackResult;
        } catch (error) {
          callbackFailed = true;
          applicationError = error;
          recordApplicationError(span, error);
          throw error;
        } finally {
          endSpan(span);
        }
      });
    } catch (error) {
      if (callbackFailed) throw applicationError;

      // A tracer/context manager can fail while cleaning up after successful
      // application work. Preserve the result rather than turning that
      // telemetry failure into a request failure.
      if (callbackCompleted) return callbackResult!;

      // Never repeat a side-effecting callback if it began but did not settle.
      if (callbackStarted) throw error;

      // Tracer acquisition/span creation failed before application code ran.
      // Execute once without tracing so observability cannot break the request.
      return callback(undefined);
    }
  };
}

export const runInSpan = createSpanRunner(() => trace.getTracer(TRACER_NAME));

export function getActiveSpanContext(): SpanContext | undefined {
  try {
    return trace.getActiveSpan()?.spanContext();
  } catch {
    return undefined;
  }
}

/**
 * Run deferred work as a separate trace linked to the initiating span.
 *
 * A link preserves navigation/correlation without making deferred work extend
 * the initiating request's timeline. The sampler still decides whether the new
 * trace is recorded, so callers cannot bypass the production trace allowlist.
 */
export async function runInLinkedRootSpan<T>(
  name: string,
  linkedSpanContext: SpanContext | undefined,
  callback: SpanCallback<T>,
  options: SpanOptions = {}
): Promise<T> {
  const linkedOptions = linkedSpanContext
    ? {
        ...options,
        links: [...(options.links ?? []), { context: linkedSpanContext }],
      }
    : options;

  return context.with(ROOT_CONTEXT, () => runInSpan(name, callback, linkedOptions));
}
